import React, { useRef } from "react"
import { useSelector } from "react-redux"
import { useEffect, useState } from "react"
import { TAfindAllApprovalUser } from "../../../services/userAPI"
import { User } from "../../../types/UserData"
import { FilterValue } from "../../../types/FilterDatas"
import DownloadCSVButton from "../../../components/DownloadCSVButton"
import { selectWaitingApprovalUserFilters } from "../../../redux/store/filters/user/waitingApprovalUserFilterSlice"
import { columnConfig } from "./components/columns"
import StickyDataTable from "../../../components/StickyDataTable"
import Filters from "./components/Filters"
import { useNavigate } from "react-router-dom"
import { isAbortError, getErrorStatus } from "../../../types/error"

const WaitingApprovalUser = () => {
  const navigate = useNavigate()
  const waitingApprovalUserFilters = useSelector(selectWaitingApprovalUserFilters)
  const abortControllerRef = useRef<AbortController | null>(null)
  const [page, setPage] = useState(1)
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [pageSize, setPageSize] = useState(PAGE_SIZES[2])
  const [totalRecords, setTotalRecords] = useState(0)
  const [initialRecords, setInitialRecords] = useState([] as User[])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [sortByColumn, setSortByColumn] = useState<string>("createdAt")
  const [sortOrder, setSortOrder] = useState<boolean>(false)

  useEffect(() => {
    handleFetchData()
  }, [page, pageSize, sortByColumn, sortOrder])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    setPage(1)
  }, [pageSize, waitingApprovalUserFilters])

  const handleFetchData = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)

    const flattenFilters = Object.entries(waitingApprovalUserFilters).reduce(
      (acc, [key, filter]) => {
        const simpleKeys = [
          "gender",
          "verification",
          "warnings",
          "platform",
          "is_spam",
          "unvisible",
          "last_login",
          "frequency",
          "country",
          "city",
          "job",
          "insta_username",
          "tiktok_username",
        ]

        if (simpleKeys.includes(key) && typeof filter === "string") {
          acc[key] = filter
        } else if (filter && typeof filter === "object") {
          const { min, max } = filter as FilterValue
          if (min) {
            acc[`min_${key}`] = min
          }
          if (max) {
            acc[`max_${key}`] = max
          }
        }

        return acc
      },
      {} as { [key: string]: string },
    )

    const params = new URLSearchParams(flattenFilters)

    const keywords = (waitingApprovalUserFilters.keywords as string[])?.map(
      (keyword) => keyword.charAt(0).toUpperCase() + keyword.slice(1),
    )
    keywords?.forEach((keywords) => {
      params.append("keywords", keywords)
    })

    const hobbies = (waitingApprovalUserFilters.hobbies as string[]).map(
      (hobby) => hobby.charAt(0) + hobby.slice(1),
    )
    hobbies.forEach((hobbies) => {
      params.append("hobbies", hobbies)
    })
    try {
      const response = await TAfindAllApprovalUser(
        page,
        pageSize,
        params.toString(),
        sortByColumn,
        sortOrder,
        abortControllerRef.current.signal,
      )
      const totalRecords = response.totalDocuments
      const data = response.verificationProfiles.map((item: any) => {
        return {
          ...item,
        }
      })
      setInitialRecords(data)
      setTotalRecords(totalRecords)
      setLoading(false)
    } catch (error: unknown) {
      if (isAbortError(error)) {
        setLoading(false)
        return
      }

      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setLoading(false)
      setError("No data found")
    }
  }

  const columns = columnConfig()

  const handleSorting = (columnAccessor: string, direction?: "asc" | "desc") => {
    if (columnAccessor === sortByColumn) {
      setSortOrder(direction === "desc")
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(direction === "desc")
    }
  }

  return (
    <div className="panel">
      <Filters
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        handleFetchData={handleFetchData}
      />
      <div className=" flex md:items-center md:flex-row flex-col gap-5">
        <div
          className="flex w-1/10 bg-gray-100 ml-4 items-center border border-gray-300 rounded-3xl mb-4 p-2 cursor-pointer"
          onClick={() => setIsFilterOpen(true)}
        >
          Filtrele
        </div>
        <div className="flex flex-row ml-auto text-center mb-4 mr-2">
          <button
            className=" inline-flex items-center justify-center ml-2 p-2 mt-6 mb-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-blue-600 hover:bg-blue-700"
            onClick={() => handleFetchData()}
          >
            Kullanıcıları Ara
          </button>
          <DownloadCSVButton
            className=" inline-flex items-center justify-center ml-2 px-2 mt-6 mb-2 border border-transparent text-sm font-medium rounded-3xl text-white bg-green-600 hover:bg-green-700"
            userData={initialRecords}
          />
        </div>
      </div>
      <div className="datatables">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
          </div>
        ) : (
          <StickyDataTable
            highlightOnHover={true}
            className="whitespace-nowrap"
            records={initialRecords}
            columns={columns}
            totalRecords={totalRecords}
            recordsPerPage={pageSize}
            page={page}
            onPageChange={(p) => setPage(p)}
            recordsPerPageOptions={PAGE_SIZES}
            onRecordsPerPageChange={setPageSize}
            sortStatus={{
              columnAccessor: sortByColumn,
              direction: sortOrder ? "desc" : "asc",
            }}
            onSortStatusChange={(e) => handleSorting(e.columnAccessor, e.direction)}
            minHeight={200}
            paginationText={({ from, to, totalRecords }) =>
              `Showing  ${from} to ${to} of ${totalRecords} entries`
            }
          />
        )}
      </div>
    </div>
  )
}

export default WaitingApprovalUser
